/**
 * Deterministic names with culture-level phonology and entity-level grammar.
 *
 * A culture key chooses the sound inventory; an entity key chooses a name
 * within it. This makes nearby names feel related without storing a language
 * or consuming generation-order-dependent RNG state.
 */
import { hashSeed } from "../rng.js";

interface Language {
  onset: readonly string[];
  vowel: readonly string[];
  coda: readonly string[];
  join: readonly string[];
  familyEnd: readonly string[];
  placeEnd: readonly string[];
  /** Gendered GIVEN-name endings (2026-07-31, wayfarer NPCs): applied over the
   *  given stem when a gender is requested; "" keeps the bare stem. */
  feminineEnd: readonly string[];
  masculineEnd: readonly string[];
}

const LANGUAGES: readonly Language[] = [
  {
    onset: ["b", "br", "d", "f", "g", "h", "k", "l", "m", "n", "r", "s", "t", "v", "w"],
    vowel: ["a", "e", "i", "o", "u", "ae"], coda: ["", "n", "r", "s", "th", "nd", "rk", "ll"],
    join: ["a", "e", "i"], familyEnd: ["en", "ard", "ell", "orn", "wick", "mere"],
    placeEnd: ["haven", "wick", "mere", "ward", "holt", "reach"],
    feminineEnd: ["a", "ia", "wen", "lyn", "eth", "ara"],
    masculineEnd: ["", "on", "ric", "ard", "orn", "an"],
  },
  {
    onset: ["", "d", "j", "k", "l", "m", "n", "p", "r", "s", "sh", "t", "v", "z"],
    vowel: ["a", "ai", "e", "ei", "i", "o", "ou", "u"], coda: ["", "m", "n", "r", "sh", "t", "l"],
    join: ["i", "o", "u"], familyEnd: ["ai", "esh", "orin", "uma", "adar", "uun"],
    placeEnd: ["ara", "esh", "oru", "sai", "uma", "adar"],
    feminineEnd: ["a", "ira", "una", "ei", "ala", "ami"],
    masculineEnd: ["", "an", "esh", "oru", "im", "ar"],
  },
  {
    onset: ["c", "ch", "d", "f", "gl", "l", "m", "n", "qu", "r", "s", "th", "v", "x"],
    vowel: ["a", "e", "ia", "i", "o", "y"], coda: ["", "c", "l", "n", "r", "s", "x"],
    join: ["a", "ia", "y"], familyEnd: ["is", "ane", "or", "yx", "enne", "ial"],
    placeEnd: ["ion", "enne", "ora", "axis", "eum", "yss"],
    feminineEnd: ["ia", "elle", "yra", "ess", "ara", "ine"],
    masculineEnd: ["", "us", "or", "an", "ex", "iel"],
  },
  {
    onset: ["b", "dr", "g", "gr", "k", "kh", "m", "n", "r", "sk", "t", "th", "v", "z"],
    vowel: ["a", "e", "i", "o", "u", "ya"], coda: ["", "g", "k", "m", "n", "r", "v", "z"],
    join: ["a", "o", "u"], familyEnd: ["ak", "ov", "ir", "un", "ar", "ek"],
    placeEnd: ["grad", "kar", "osk", "var", "un", "gor"],
    feminineEnd: ["a", "ka", "ina", "ya", "eva", "mira"],
    masculineEnd: ["", "ok", "an", "ir", "ek", "or"],
  },
  {
    onset: ["", "h", "k", "l", "m", "n", "p", "r", "s", "t", "w", "y"],
    vowel: ["a", "e", "i", "o", "u"], coda: ["", "", "h", "m", "n", "r"],
    join: ["a", "i", "o"], familyEnd: ["ari", "eno", "ima", "oru", "aya", "une"],
    placeEnd: ["ama", "iri", "ora", "una", "eno", "ashi"],
    feminineEnd: ["a", "ina", "iko", "ani", "aya", "une"],
    masculineEnd: ["", "o", "aru", "iro", "an", "u"],
  },
  {
    onset: ["b", "d", "f", "g", "j", "k", "l", "m", "n", "p", "r", "s", "t", "v"],
    vowel: ["a", "e", "i", "o", "u", "ae", "io"], coda: ["", "d", "l", "m", "n", "r", "s"],
    join: ["e", "i", "o"], familyEnd: ["son", "sen", "dal", "lund", "mark", "ren"],
    placeEnd: ["dal", "lund", "mark", "vik", "fjord", "heim"],
    feminineEnd: ["a", "rid", "hild", "run", "vi", "sa"],
    masculineEnd: ["", "ar", "or", "ulf", "sten", "en"],
  },
] as const;

const LANDMARKS = ["Beacon", "Bridge", "Crossing", "Gate", "Harbor", "Hollow", "March", "Reach", "Spire", "Vale", "Watch", "Well"] as const;
const LANDMARK_PREFIX = ["Amber", "Ashen", "Bright", "Cloud", "Dawn", "Ember", "Far", "Glass", "Iron", "Moon", "Quiet", "Red", "Star", "Storm", "Sun", "Verdant"] as const;
const HISTORY = ["Concord", "First Light", "Freehold", "Lastlight", "New Promise", "Pilgrim's Rest", "Reunion", "Second Dawn", "Wayfarer's Rest"] as const;
const ROUTES = ["Anchor", "Causeway", "Crossroads", "Landing", "Pass", "Port", "Roadstead", "Station", "Traverse"] as const;

function index(key: string, count: number): number {
  return count <= 1 ? 0 : hashSeed(key) % count;
}

function choose<T>(key: string, values: readonly T[]): T {
  return values[index(key, values.length)];
}

function title(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Planet portion of a W4 settlement identity; legacy callers retain local culture. */
export function namingCulture(seed: string): string {
  const settlement = seed.indexOf(":stl:");
  if (settlement > 0) return seed.slice(0, settlement);
  const town = seed.indexOf(":town:");
  if (town > 0) return seed.slice(0, town);
  const npc = seed.indexOf(":npc:");
  return npc > 0 ? seed.slice(0, npc) : seed;
}

function language(cultureKey: string): Language {
  return LANGUAGES[index(`${namingCulture(cultureKey)}:language`, LANGUAGES.length)];
}

function syllable(lang: Language, key: string, allowCoda: boolean): string {
  const onset = choose(`${key}:o`, lang.onset);
  const vowel = choose(`${key}:v`, lang.vowel);
  const coda = allowCoda ? choose(`${key}:c`, lang.coda) : "";
  return onset + vowel + coda;
}

function word(lang: Language, key: string, min = 2, max = 3): string {
  const count = min + index(`${key}:length`, max - min + 1);
  let out = "";
  for (let i = 0; i < count; i++) out += syllable(lang, `${key}:${i}`, i === count - 1);
  // Smooth the few ugly seams a combinatorial phoneme deck can produce.
  return title(out.replace(/([aeiou])\1+/g, "$1").replace(/([bcdfghjklmnpqrstvwxyz])\1+/g, "$1"));
}

function familyName(lang: Language, householdKey: string): string {
  const root = word(lang, `${householdKey}:family`, 1, 2);
  const end = choose(`${householdKey}:family:end`, lang.familyEnd);
  return title(root.replace(/[aeiouy]+$/i, "") + end);
}

/** NPC gender axis (2026-07-31 wayfarer NPCs): deterministic ~50/50 from the
 *  person key — THE one rule every roster and style roll derives from, so a
 *  resident's name and body always agree. */
export type PersonGender = "f" | "m";
export function personGender(personKey: string): PersonGender {
  return (hashSeed(`${personKey}:gender`) & 1) === 1 ? "f" : "m";
}

/** A local given name plus a stable household name shared by co-residents.
 *  With a `gender`, the given name takes that language's gendered ending
 *  (feminine for the wayfarers); omitted → the legacy ungendered stem, so
 *  existing callers stay byte-identical. */
export function personName(personKey: string, householdKey = personKey,
  cultureKey = namingCulture(personKey), gender?: PersonGender): string {
  const lang = language(cultureKey);
  const stem = word(lang, `${personKey}:given`, 2, index(`${personKey}:given:long`, 5) === 0 ? 3 : 2);
  let given = stem;
  if (gender !== undefined) {
    const end = choose(`${personKey}:given:end`, gender === "f" ? lang.feminineEnd : lang.masculineEnd);
    if (end !== "") {
      const base = stem.replace(/[aeiouy]+$/i, ""); // all-vowel stems keep their vowels
      // Same seam smoothing word() applies — the join can double a consonant
      // ("Kubr"+"ric" → "Kubrric") or a vowel; collapse both.
      given = title(((base === "" ? stem : base) + end)
        .replace(/([aeiou])\1+/g, "$1").replace(/([bcdfghjklmnpqrstvwxyz])\1+/g, "$1"));
    }
  }
  return `${given} ${familyName(lang, householdKey)}`;
}

// ---------------------------------------------------------------------------
// Celestial naming (2026-08-17): systems and their worlds.
//
// Two layers of kinship, both hash-keyed (no generation-order RNG):
//  - The REGION key picks the Language, so neighboring systems sound like one
//    "constellation" of related names and crossing a region changes tongues.
//  - Each system hashes to ONE family trait — a vowel-harmony class, an onset
//    family, or a coda style — applied as a 70% BIAS, so sibling worlds
//    half-rhyme with their star without ever matching outright. (Precedent:
//    the IAU's per-planet moon themes and NameExoWorlds' themed systems.)
// ---------------------------------------------------------------------------

interface CelestialFamily {
  lang: Language;
  /** 0 vowel harmony · 1 shared onset family · 2 shared coda style. */
  trait: number;
  vowels: readonly string[];
  onsets: readonly string[];
  codas: readonly string[];
}

function celestialFamily(systemKey: string, regionKey: string): CelestialFamily {
  const lang = LANGUAGES[index(`${regionKey}:celestial:language`, LANGUAGES.length)];
  const fam = `${systemKey}:family`;
  return {
    lang,
    trait: index(`${fam}:trait`, 3),
    vowels: [choose(`${fam}:v0`, lang.vowel), choose(`${fam}:v1`, lang.vowel)],
    onsets: [choose(`${fam}:o0`, lang.onset), choose(`${fam}:o1`, lang.onset), choose(`${fam}:o2`, lang.onset)],
    codas: [choose(`${fam}:c0`, lang.coda), choose(`${fam}:c1`, lang.coda)],
  };
}

/** Deck seams can still pile up sounds the smoothing pass keeps; a name that
 *  fails re-rolls with the attempt in the key, so the gate stays deterministic. */
function pronounceable(name: string): boolean {
  return name.length >= 3 && name.length <= 11 &&
    !/[bcdfghjklmnpqrstvwxz]{4}/i.test(name) &&
    !/(?:th|sh|ch|kh)[bcdfgjkmnpqstvwxz]/i.test(name) &&
    !/[bcdfghjklmnpqrstvwxz]{3}$/i.test(name) &&
    !/[aeiou]{4}/i.test(name) &&
    !/(...).*\1/i.test(name); // sing-song repeats ("Ryagryagra", "Tyaryar")
}

function celestialWord(fam: CelestialFamily, key: string, minSyl: number, maxSyl: number): string {
  let out = "";
  for (let attempt = 0; attempt < 6; attempt++) {
    const k = attempt === 0 ? key : `${key}:retry${attempt}`;
    const count = minSyl + index(`${k}:length`, maxSyl - minSyl + 1);
    out = "";
    for (let i = 0; i < count; i++) {
      const sk = `${k}:${i}`;
      const last = i === count - 1;
      let onset = choose(`${sk}:o`, fam.lang.onset);
      let vowel = choose(`${sk}:v`, fam.lang.vowel);
      let coda = last ? choose(`${sk}:c`, fam.lang.coda) : "";
      // The family trait lands on ~70% of draws — kinship, never uniformity.
      if (index(`${sk}:famBias`, 10) < 7) {
        if (fam.trait === 0) vowel = choose(`${sk}:v`, fam.vowels);
        else if (fam.trait === 1 && i === 0) onset = choose(`${sk}:o`, fam.onsets);
        else if (fam.trait === 2 && last) coda = choose(`${sk}:c`, fam.codas);
      }
      out += onset + vowel + coda;
    }
    out = title(out.replace(/([aeiou])\1+/g, "$1").replace(/([bcdfghjklmnpqrstvwxyz])\1+/g, "$1"));
    if (pronounceable(out)) return out;
  }
  return out; // deterministic last resort — gate failures are already rare
}

/** Star-system name: the region's tongue, colored by this system's family trait. */
export function generatedSystemName(systemKey: string, regionKey = systemKey): string {
  return celestialWord(celestialFamily(systemKey, regionKey), `${systemKey}:sysname`, 2, 3);
}

/** A named world. Same language and family trait as its star, so siblings
 *  half-rhyme; `variant` re-rolls a rare in-system duplicate. */
export function generatedPlanetName(
  systemKey: string, planetIndex: number, regionKey = systemKey, variant = 0,
): string {
  const key = `${systemKey}:planet:${planetIndex}:name${variant > 0 ? `:v${variant}` : ""}`;
  return celestialWord(celestialFamily(systemKey, regionKey), key, 2, 3);
}

/**
 * Settlement traditions are deliberately mixed: invented local words,
 * compounds, founder commemorations, routes, and remembered events.
 */
export function generatedSettlementName(seed: string): string {
  const lang = language(seed);
  const style = index(`${seed}:settlement:tradition`, 7);
  if (style === 0) {
    const root = word(lang, `${seed}:place:root`, 1, 2).replace(/[aeiouy]+$/i, "");
    return title(root + choose(`${seed}:place:end`, lang.placeEnd));
  }
  if (style === 1) return choose(`${seed}:place:prefix`, LANDMARK_PREFIX) + choose(`${seed}:place:landmark`, LANDMARKS);
  if (style === 2) return `${choose(`${seed}:place:prefix`, LANDMARK_PREFIX)} ${choose(`${seed}:place:landmark`, LANDMARKS)}`;
  if (style === 3) return `${familyName(lang, `${seed}:founder`)} ${choose(`${seed}:place:route`, ROUTES)}`;
  if (style === 4) return choose(`${seed}:place:history`, HISTORY);
  if (style === 5) return `${word(lang, `${seed}:place:saint`, 2, 2)}'s ${choose(`${seed}:place:landmark`, LANDMARKS)}`;
  const root = word(lang, `${seed}:place:root`, 1, 2).replace(/[aeiouy]+$/i, "");
  return title(root + choose(`${seed}:place:join`, lang.join) + choose(`${seed}:place:end`, lang.placeEnd));
}
